import React, { useCallback, useMemo, useState } from 'react';
import { Box, List, useResponsive } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { EMPTY_FIELD_VALUE, SHORT_DATE_FORMAT } from '@/common/constants';
import FilePreview from '@/modules/MyDocuments/components/FilePreview/FilePreview';
import { LettersOfMedicalNecessity } from '@/modules/MyDocuments/myDocuments.types';

import { LettersOfMedicalNecessityClaims } from '../LettersOfMedicalNecessityClaims/LettersOfMedicalNecessityClaims';
import { LettersOfMedicalNecessityClaimsModal } from '../LettersOfMedicalNecessityClaims/LettersOfMedicalNecessityClaimsModal';

interface Props extends LettersOfMedicalNecessity {
  onView: (id?: string) => void;
  onEdit: (id?: string) => void;
  onDelete: (id?: string) => void;
  isHighlighted?: boolean;
}

const LettersOfMedicalNecessityListItem: React.FC<Props> = ({
  id,
  name,
  description,
  claims,
  validFrom,
  validTo,
  onView,
  onEdit,
  onDelete,
  fileUrl,
  isHighlighted,
}) => {
  const { isMobile } = useResponsive();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const dates = useMemo(() => {
    if (!validFrom && !validTo) {
      return EMPTY_FIELD_VALUE;
    }

    const formattedValidFrom = validFrom ? dayjs(validFrom).format(SHORT_DATE_FORMAT) : EMPTY_FIELD_VALUE;
    const formattedValidTo = validTo ? dayjs(validTo).format(SHORT_DATE_FORMAT) : EMPTY_FIELD_VALUE;
    return `${formattedValidFrom} - ${formattedValidTo}`;
  }, [validFrom, validTo]);

  const handleView = useCallback(() => {
    onView(id);
  }, [id, onView]);

  const handleEdit = useCallback(() => {
    onEdit(id);
  }, [id, onEdit]);

  const handleDelete = useCallback(() => {
    onDelete(id);
  }, [id, onDelete]);

  if (isMobile) {
    return (
      <List.Item
        isHighlighted={isHighlighted}
        direction="column"
        pad={isMobile ? 'spacing12' : { vertical: 'spacing12', horizontal: 'spacing24' }}
        testId={`lmn-list_item_${id}`}
      >
        <Box fill direction="row" align="center" width="100%">
          <FilePreview
            testId={`lmn-list_item_${id}_file-preview`}
            fileUrl={fileUrl}
            width="45px"
            height="45px"
            margin={{ right: 'spacing12' }}
            onClick={handleView}
          />
          <Box direction="column" fill>
            <List.Item.Text
              testId={`lmn-list_item_${id}_name`}
              title={name}
              truncate
              onClick={handleView}
            >
              {name}
            </List.Item.Text>

            <List.Item.Text
              margin={{ top: 'spacing2' }}
              testId={`lmn-list_item_${id}_description`}
              weight={400}
              onClick={handleView}
            >
              {description}
            </List.Item.Text>
          </Box>

          <List.Item.Options>
            <List.Item.Options.Item testId={`lmn-list_item_${id}_view-action`} onClick={handleView}>
              View
            </List.Item.Options.Item>

            <List.Item.Options.Item testId={`lmn-list_item_${id}_edit-action`} onClick={handleEdit}>
              Edit
            </List.Item.Options.Item>

            <List.Item.Options.Item
              testId={`lmn-list_item_${id}_delete-action`}
              color="danger"
              weight={700}
              onClick={handleDelete}
            >
              Delete
            </List.Item.Options.Item>
          </List.Item.Options>
        </Box>
        <Box height="1px" background="border2" width="100%" margin={{ vertical: 'spacing12' }} />
        <Box direction="row" fill justify="between">
          <Box fill>
            <List.Item.Text testId={`lmn-list_item_${id}_claim`} weight={400}>
              <LettersOfMedicalNecessityClaims claims={claims} onShowClaims={() => setIsModalVisible(true)} />
            </List.Item.Text>
          </Box>
          <div>
            <List.Item.Text weight={700} testId={`lmn-list_item_${id}_dates`}>
              {dates}
            </List.Item.Text>
          </div>
        </Box>
        <LettersOfMedicalNecessityClaimsModal
          visible={isModalVisible}
          onSetVisible={setIsModalVisible}
          claims={claims || []}
          lmnName={description || 'LMN'}
        />
      </List.Item>
    );
  }
  return (
    <List.Item
      isHighlighted={isHighlighted}
      pad={{ vertical: 'spacing12', horizontal: 'spacing24' }}
      testId={`lmn-list_item_${id}`}
    >
      <Box
        style={{ flex: 0.6 }}
        justify="center"
        onClick={handleView}
      >
        <FilePreview
          testId={`lmn-list_item_${id}_file-preview`}
          fileUrl={fileUrl}
          width="61px"
          height="82px"
        />
      </Box>

      <List.Item.Text
        testId={`lmn-list_item_${id}_name`}
        flex={1}
        title={name}
        truncate
        onClick={handleView}
      >
        {name}
      </List.Item.Text>

      <List.Item.Text
        testId={`lmn-list_item_${id}_description`}
        weight={400}
        flex={1.8}
        title={description}
        truncate
        onClick={handleView}
      >
        {description}
      </List.Item.Text>

      <List.Item.Text testId={`lmn-list_item_${id}_claim`} weight={400} flex={1}>
        <LettersOfMedicalNecessityClaims claims={claims} onShowClaims={() => setIsModalVisible(true)} />
      </List.Item.Text>

      <List.Item.Text testId={`lmn-list_item_${id}_dates`} weight={400} flex={1}>
        {dates}
      </List.Item.Text>

      <Box style={{ flex: 0.5 }} align="end">
        <List.Item.Options>
          <List.Item.Options.Item testId={`lmn-list_item_${id}_view-action`} onClick={handleView}>
            View
          </List.Item.Options.Item>

          <List.Item.Options.Item testId={`lmn-list_item_${id}_edit-action`} onClick={handleEdit}>
            Edit
          </List.Item.Options.Item>

          <List.Item.Options.Item
            testId={`lmn-list_item_${id}_delete-action`}
            color="danger"
            onClick={handleDelete}
          >
            Delete
          </List.Item.Options.Item>
        </List.Item.Options>
      </Box>
      <LettersOfMedicalNecessityClaimsModal
        visible={isModalVisible}
        onSetVisible={setIsModalVisible}
        claims={claims || []}
        lmnName={name || 'LMN'}
      />
    </List.Item>
  );
};

export default LettersOfMedicalNecessityListItem;
